import React, { createContext, useContext, useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type ToastVariant = 'default' | 'destructive' | 'success';

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

// Create a variable to store the context value
// This allows us to access it from outside React components
let TOAST_CONTEXT: ToastContextType | undefined = undefined;

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (props: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, ...props }]);
    
    // Auto dismiss after 5 seconds
    setTimeout(() => {
      dismiss(id);
    }, 5000);
  };

  const dismiss = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const contextValue = { toasts, addToast, dismiss };
  
  // Store the context value in the global variable
  TOAST_CONTEXT = contextValue;

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  // Add a toast method to the returned object
  return {
    ...context,
    toast: (props: Omit<Toast, 'id'>) => context.addToast(props)
  };
};

// Export a standalone toast function that uses the TOAST_CONTEXT
export const toast = (props: Omit<Toast, 'id'>) => {
  if (TOAST_CONTEXT) {
    TOAST_CONTEXT.addToast(props);
  } else {
    console.warn('Toast was called before ToastProvider was mounted');
  }
};

// Extracted ToastContainer to use at the provider level
const ToastContainer: React.FC<{ toasts: Toast[]; dismiss: (id: string) => void }> = ({ 
  toasts, 
  dismiss 
}) => {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md">
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Renamed from Toast to ToastItem to avoid naming conflicts
const ToastItem: React.FC<{ toast: Toast; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const getVariantClasses = (): string => {
    switch (toast.variant) {
      case 'destructive':
        return 'bg-red-600 text-white';
      case 'success':
        return 'bg-green-600 text-white';
      default:
        return 'bg-white text-gray-900 border border-gray-200';
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      className={`rounded-lg shadow-lg p-4 flex items-start ${getVariantClasses()}`}
    >
      <div className="flex-1 mr-2">
        <h3 className="font-medium">{toast.title}</h3>
        {toast.description && <p className="text-sm mt-1">{toast.description}</p>}
      </div>
      <button
        onClick={onDismiss}
        className="text-gray-500 hover:text-gray-700"
        aria-label="Dismiss notification"
      >
        <X size={18} />
      </button>
    </motion.div>
  );
}; 